// ─────────────────────────────────────────────────────────────
// Modals — all overlay dialogs for Maps Discover Studio.
//
// Exports:
//   FullscreenImageModal  — zoom/pan image viewer overlay
//   LibrarySelectModal    — pick an image from the library
//   UploadImageModal      — upload form with metadata
//   ParameterEditorModal  — edit key=value script parameters
//   SaveScriptModal       — first-save dialog (title + description + category)
//   SaveVersionDialog     — radio-card chooser opened when Save is clicked on
//                           an already-saved script: "Save changes to v{N}
//                           (current)" vs. "Create new v{N+1}". The notes
//                           field is enabled only when the create-new card is
//                           selected. Save's own button handles the SAVE
//                           direction (commit to disk); the LOAD direction
//                           (pull an existing version into the editor) lives
//                           on the New Script split-button's chevron menu.
// ─────────────────────────────────────────────────────────────

import { useState, useRef, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { imageApi } from "../../api/client";
import type { AuthUser, UserImage, LibraryImage } from "../../api/client";
import type { PlaceholderImage } from "./types";
import { MetadataEditForm } from "./MetadataChip";
import { DerivedTagChips } from "./DerivedTagChips";
import { CloudUploadConfirmDialog } from "./CloudUploadConfirmDialog";

// ── Auth user helper ──────────────────────────────────────────

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

// ── Shared dialog title with close button ────────────────────

function ModalTitle({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        px: 2,
      }}
    >
      <Typography variant="h3" component="span">
        {title}
      </Typography>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseOutlinedIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
  );
}

// ─────────────────────────────────────────────────────────────
// FullscreenImageModal
// ─────────────────────────────────────────────────────────────

interface FullscreenImageModalProps {
  open: boolean;
  onClose: () => void;
  imageName?: string;
  /** Thumbnail blob URL — shown immediately as a placeholder while full-res loads. */
  blobUrl?: string;
  /** Image ID used to fetch the full-resolution version. */
  imageId?: number;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;

export function FullscreenImageModal({
  open,
  onClose,
  imageName = "Image",
  blobUrl,
  imageId,
}: FullscreenImageModalProps) {
  const [zoom, setZoom] = useState(1);

  // Full-resolution blob URL — fetched when the modal opens.
  // Falls back to the thumbnail blobUrl while loading.
  const [fullResBlobUrl, setFullResBlobUrl] = useState<string | null>(null);
  const [fullResLoading, setFullResLoading] = useState(false);

  useEffect(() => {
    if (!open || imageId == null) return;
    let cancelled = false;
    setFullResBlobUrl(null);
    setFullResLoading(true);
    imageApi.getImageUrl(imageId).then((url) => {
      if (!cancelled) {
        setFullResBlobUrl(url);
        setFullResLoading(false);
      } else {
        URL.revokeObjectURL(url);
      }
    }).catch(() => {
      if (!cancelled) setFullResLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, imageId]);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Refs for drag tracking (avoid stale closures in mousemove)
  const dragStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  // Ref to the viewport container for bounding-rect math
  const containerRef = useRef<HTMLDivElement>(null);
  // Single source of truth for the current transform — avoids stale reads
  // inside the wheel handler where nested functional setState is unreliable.
  const transformRef = useRef({ zoom: 1, tx: 0, ty: 0 });

  function resetView() {
    transformRef.current = { zoom: 1, tx: 0, ty: 0 };
    setZoom(1);
    setTranslateX(0);
    setTranslateY(0);
  }

  function handleClose() {
    resetView();
    if (fullResBlobUrl) {
      URL.revokeObjectURL(fullResBlobUrl);
      setFullResBlobUrl(null);
    }
    onClose();
  }

  function handleZoomIn() {
    const next = Math.min(transformRef.current.zoom + 0.25, MAX_ZOOM);
    transformRef.current.zoom = next;
    setZoom(next);
  }

  function handleZoomOut() {
    const next = Math.max(transformRef.current.zoom - 0.25, MIN_ZOOM);
    transformRef.current.zoom = next;
    if (next <= 1) {
      transformRef.current.tx = 0;
      transformRef.current.ty = 0;
      setTranslateX(0);
      setTranslateY(0);
    }
    setZoom(next);
  }

  // ── Mouse wheel zoom (cursor-centred) ────────────────────────
  // Reads from transformRef so we always have the true current values —
  // calling setState inside another setState's updater is a side effect
  // React may double-invoke, corrupting the translate correction.
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    const { zoom: prevZoom, tx: prevTx, ty: prevTy } = transformRef.current;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prevZoom * factor));
    const scale = nextZoom / prevZoom;

    // Keep the image point under the cursor fixed:
    // newTranslate = (mouse - center) * (1 - scale) + prevTranslate * scale
    let newTx = (mouseX - cx) * (1 - scale) + prevTx * scale;
    let newTy = (mouseY - cy) * (1 - scale) + prevTy * scale;
    if (nextZoom <= 1) { newTx = 0; newTy = 0; }

    transformRef.current = { zoom: nextZoom, tx: newTx, ty: newTy };
    setZoom(nextZoom);
    setTranslateX(newTx);
    setTranslateY(newTy);
  }, []);

  // ── Drag-to-pan ──────────────────────────────────────────────
  function handleMouseDown(e: React.MouseEvent) {
    // Only primary button
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      tx: translateX,
      ty: translateY,
    };
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!isDragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const newTx = dragStartRef.current.tx + dx;
    const newTy = dragStartRef.current.ty + dy;
    transformRef.current.tx = newTx;
    transformRef.current.ty = newTy;
    setTranslateX(newTx);
    setTranslateY(newTy);
  }

  function handleMouseUp() {
    setIsDragging(false);
    dragStartRef.current = null;
  }

  // Also release drag if mouse leaves the container
  function handleMouseLeave() {
    if (isDragging) {
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }

  // ── Double-click to reset ────────────────────────────────────
  function handleDoubleClick() {
    resetView();
  }

  // The transform uses transform-origin: center (default) so we only
  // need to apply translate for pan. For cursor-centred wheel zoom we
  // compute the corrective translate above.
  const transform = `translate(${translateX}px, ${translateY}px) scale(${zoom})`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { height: "90vh", display: "flex", flexDirection: "column" } }}
    >
      <ModalTitle title={imageName} onClose={handleClose} />
      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `
            linear-gradient(45deg, #666 25%, transparent 25%),
            linear-gradient(-45deg, #666 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #666 75%),
            linear-gradient(-45deg, transparent 75%, #666 75%)
          `,
          backgroundSize: "16px 16px",
          backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
          backgroundColor: "#444",
          overflow: "hidden",
          p: 1,
        }}
      >
        <Box
          ref={containerRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onDoubleClick={handleDoubleClick}
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDragging ? "grabbing" : "grab",
            overflow: "hidden",
            userSelect: "none",
          }}
        >
          {/* Show full-res when ready, thumbnail as placeholder while loading */}
          {(fullResBlobUrl ?? blobUrl) ? (
            <Box
              component="img"
              src={fullResBlobUrl ?? blobUrl}
              alt={imageName}
              draggable={false}
              sx={{
                transform,
                transition: isDragging ? "none" : "transform 150ms",
                transformOrigin: "center",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                pointerEvents: "none",
              }}
            />
          ) : (
            <Box
              sx={{
                transform,
                transition: isDragging ? "none" : "transform 150ms",
                transformOrigin: "center",
                bgcolor: "action.hover",
                borderRadius: 1,
                width: 600,
                height: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                pointerEvents: "none",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <ImageOutlinedIcon sx={{ fontSize: 64, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {imageName}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Loading image…
                </Typography>
              </Box>
            </Box>
          )}

          {/* Full-res loading indicator — centred overlay so it's unmissable */}
          {fullResLoading && blobUrl && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "rgba(0,0,0,0.72)",
                color: "white",
                borderRadius: 2,
                px: 3,
                py: 2.5,
                minWidth: 220,
                boxShadow: 6,
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={36} sx={{ color: "white" }} />
              <Typography variant="body2" sx={{ color: "white", fontWeight: 500 }}>
                Loading full resolution…
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Button size="small" onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM}>Zoom Out</Button>
        <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <Button size="small" onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM}>Zoom In</Button>
        <Button size="small" onClick={resetView}>Reset</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// LibrarySelectModal
// ─────────────────────────────────────────────────────────────

interface LibrarySelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (image: PlaceholderImage) => void;
}

// Single image tile with lazy thumbnail fetch
function ImageTile({ img, onSelect }: { img: UserImage | LibraryImage; onSelect: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    imageApi.getThumbnailUrl(img.id).then((url) => {
      if (!cancelled) setBlobUrl(url);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [img.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      component="button"
      onClick={onSelect}
      sx={{
        // Native <button> resets text color to the UA default (dark),
        // which is invisible on the dark tile in dark mode. Anchor the
        // tile's text color to the theme so descendant Typography that
        // inherits (the image-name caption below) reads correctly in
        // both light and dark mode.
        color: "text.primary",
        font: "inherit",
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
        cursor: "pointer",
        textAlign: "left",
        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "2px" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: 80,
          bgcolor: "background.default",
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          overflow: "hidden",
        }}
      >
        {blobUrl
          ? <Box component="img" src={blobUrl} alt={img.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <ImageOutlinedIcon sx={{ color: "text.disabled", fontSize: 32 }} />
        }
      </Box>
      <Typography variant="caption" color="text.primary" sx={{ display: "block", fontWeight: 500, mb: 0.25, wordBreak: "break-all" }}>
        {img.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {img.image_type}
      </Typography>
      {img.derived_tags && (
        <Box sx={{ mt: 0.5 }}>
          <DerivedTagChips derived={img.derived_tags} compact />
        </Box>
      )}
    </Box>
  );
}

export function LibrarySelectModal({ open, onClose, onSelect }: LibrarySelectModalProps) {
  const [myImages, setMyImages] = useState<UserImage[]>([]);
  const [sharedImages, setSharedImages] = useState<LibraryImage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([imageApi.getMyImages(), imageApi.getSharedImages()])
      .then(([mine, shared]) => { setMyImages(mine); setSharedImages(shared); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  function handleSelect(img: UserImage | LibraryImage, source: "user" | "library") {
    onSelect({
      id: String(img.id),
      name: img.name,
      description: img.description ?? "",
      tags: img.tags ?? null,
      type: img.image_type as PlaceholderImage["type"],
      source,
      acquisition_metadata: img.acquisition_metadata ?? null,
      metadata_source: img.metadata_source ?? null,
      derived_tags: img.derived_tags ?? null,
    });
    onClose();
  }

  // Deduplicate: shared images the user also owns are already in myImages
  const authUser = getAuthUser();
  const myImageIds = new Set(myImages.map((img) => img.id));
  const sharedOnly = sharedImages.filter(
    (img) => !myImageIds.has(img.id) && !(authUser && img.user_id === authUser.id)
  );
  const allImages = [...myImages, ...sharedOnly];
  const hasShared = sharedOnly.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <ModalTitle title="Select Image" onClose={onClose} />
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : allImages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No images found. Upload one from the Image Library tab.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 1.5, pt: 1 }}>
              {myImages.map((img) => (
                <ImageTile key={`my-${img.id}`} img={img} onSelect={() => handleSelect(img, "user")} />
              ))}
              {sharedOnly.map((img) => (
                <ImageTile key={`shared-${img.id}`} img={img} onSelect={() => handleSelect(img, "library")} />
              ))}
            </Box>

            {hasShared && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.75,
                  mt: 2,
                  p: 1.25,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", mt: 0.125, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  Shared images may become unavailable if the owner removes them. Save to My Images to keep permanent access.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// UploadImageModal
// ─────────────────────────────────────────────────────────────

interface UploadImageModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the newly created image after a successful upload. */
  onUpload: (image: UserImage) => void;
}

export function UploadImageModal({ open, onClose, onUpload }: UploadImageModalProps) {
  // Admins default to sharing; regular users default to private
  const isAdmin = getAuthUser()?.is_admin ?? false;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Friendly "is this OK for the cloud?" confirmation gate shown in front of
  // the actual upload. Persistent-library uploads only — the ephemeral
  // temp-upload-for-run picker is intentionally NOT gated (it fires too often
  // during iteration). The form state is preserved while this is open so
  // "Not now" cleanly returns the user to the form.
  const [showCloudConfirm, setShowCloudConfirm] = useState(false);

  /**
   * Two-step flow: when an upload returns metadata_source='none' (no FEI tag),
   * we keep the modal open and switch to a metadata-entry step instead of
   * immediately closing. Users can fill in pixel size etc., or skip.
   */
  const [uploadedImage, setUploadedImage] = useState<UserImage | null>(null);

  // Validation helpers
  const descTooShort = description.length > 0 && description.length < 10;
  const canSubmit =
    !!selectedFile &&
    name.trim().length > 0 &&
    description.length >= 10 &&
    !isUploading;

  function resetForm() {
    setName("");
    setDescription("");
    setTags("");
    setIsGlobal(isAdmin);
    setSelectedFile(null);
    setError(null);
    setIsUploading(false);
    setUploadedImage(null);
    setShowCloudConfirm(false);
  }

  function handleClose() {
    if (isUploading) return;
    // If the user closes while in the metadata step, still notify the parent
    // about the uploaded image so it appears in the list.
    if (uploadedImage) onUpload(uploadedImage);
    resetForm();
    onClose();
  }

  // Open the cloud-OK confirmation gate. Triggered by the Upload button so the
  // user gets a friendly reminder before anything hits the network.
  function requestUpload() {
    if (!canSubmit) return;
    setShowCloudConfirm(true);
  }

  // User confirmed the image is OK to upload — close the gate and proceed with
  // the existing, unchanged upload logic.
  function confirmUpload() {
    setShowCloudConfirm(false);
    void handleSubmit();
  }

  async function handleSubmit() {
    if (!canSubmit || !selectedFile) return;
    setIsUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("name", name.trim());
      fd.append("description", description);
      // image_type omitted — server defaults / derives modality from FEI metadata.
      if (tags.trim()) fd.append("tags", tags.trim());
      fd.append("is_global", String(isGlobal));

      const uploaded = await imageApi.upload(fd);

      // If the file carried FEI metadata, we're done — close immediately.
      // Otherwise, surface the manual-entry step before closing.
      if ((uploaded.metadata_source ?? "none") !== "none") {
        resetForm();
        onUpload(uploaded);
        onClose();
      } else {
        setUploadedImage(uploaded);
        setIsUploading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setIsUploading(false);
    }
  }

  function finishWithMetadata(updated?: UserImage | null) {
    if (updated) onUpload(updated);
    else if (uploadedImage) onUpload(uploadedImage);
    resetForm();
    onClose();
  }

  function applyFile(file: File) {
    setSelectedFile(file);
    if (!name) setName(file.name.replace(/\.[^.]+$/, ""));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) applyFile(file);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) applyFile(file);
  }

  // Step 2 — image uploaded but no metadata extracted. Offer manual entry.
  if (uploadedImage) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <ModalTitle title="Add acquisition metadata?" onClose={handleClose} />
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{uploadedImage.original_filename}</strong> uploaded successfully, but no
            acquisition metadata was detected. Measurement scripts will report results in pixels
            unless you provide a pixel size. Add it now or skip — you can edit it any time from
            the image's Details panel.
          </Alert>
          <MetadataEditForm
            imageId={uploadedImage.id}
            initial={uploadedImage.acquisition_metadata ?? null}
            currentSource={(uploadedImage.metadata_source ?? "none") as "extracted" | "manual" | "partial" | "none"}
            onCancel={() => finishWithMetadata()}
            onSaved={() => finishWithMetadata()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => finishWithMetadata()}>Skip for now</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <ModalTitle title="Upload Image" onClose={handleClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* Drop zone */}
          <Box
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed",
              borderColor: isDragOver ? "primary.main" : "divider",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              bgcolor: isDragOver ? "action.hover" : "background.default",
              transition: "all 150ms",
              cursor: "pointer",
            }}
            component="label"
          >
            <input
              type="file"
              accept=".tif,.tiff,.jpg,.jpeg,.png"
              hidden
              onChange={handleFileInput}
            />
            <ImageOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1, display: "block", mx: "auto" }} />
            {selectedFile ? (
              <Typography variant="body2" color="primary.main">{selectedFile.name}</Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop an image here
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  or click to browse — TIFF or JPEG only
                </Typography>
              </>
            )}
          </Box>

          <TextField
            label="Image name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={isUploading}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            required
            disabled={isUploading}
            error={descTooShort}
            helperText={
              descTooShort
                ? `${description.length}/10 characters minimum`
                : `${description.length} characters`
            }
          />
          <TextField
            label="Tags (optional)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            fullWidth
            disabled={isUploading}
            helperText="Comma-separated, e.g. alloy, steel, grain-boundary"
          />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  size="small"
                  disabled={isUploading}
                />
              }
              label={
                <Typography variant="body2">Share with everyone</Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 5.5, mt: -0.5 }}>
              Visible to all users in the Shared tab.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={requestUpload}
          disabled={!canSubmit}
          startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {isUploading ? "Uploading…" : "Upload"}
        </Button>
      </DialogActions>

      {/* Friendly cloud-OK confirmation gate — shown in front of the upload. */}
      <CloudUploadConfirmDialog
        open={showCloudConfirm}
        onCancel={() => setShowCloudConfirm(false)}
        onConfirm={confirmUpload}
      />
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// ParameterEditorModal
// ─────────────────────────────────────────────────────────────

interface Param { key: string; value: string; }

interface ParameterEditorModalProps {
  open: boolean;
  onClose: () => void;
  initialParams?: Param[];
  onSave: (params: Param[]) => void;
}

export function ParameterEditorModal({
  open,
  onClose,
  initialParams = [],
  onSave,
}: ParameterEditorModalProps) {
  const [params, setParams] = useState<Param[]>(
    initialParams.length > 0
      ? initialParams
      : [{ key: "threshold", value: "128" }]
  );

  function addParam() {
    setParams((prev) => [...prev, { key: "", value: "" }]);
  }

  function removeParam(index: number) {
    setParams((prev) => prev.filter((_, i) => i !== index));
  }

  function updateParam(index: number, field: keyof Param, val: string) {
    setParams((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: val } : p))
    );
  }

  function handleSave() {
    onSave(params.filter((p) => p.key.trim() !== ""));
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ModalTitle title="Edit Parameters" onClose={onClose} />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define script parameters that will be injected as a dict at runtime.
        </Typography>
        <Stack spacing={1}>
          {params.map((param, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                label="Key"
                value={param.key}
                onChange={(e) => updateParam(i, "key", e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Value"
                value={param.value}
                onChange={(e) => updateParam(i, "value", e.target.value)}
                sx={{ flex: 1 }}
              />
              <Tooltip title="Remove parameter">
                <IconButton size="small" onClick={() => removeParam(i)} aria-label="Remove parameter">
                  <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={addParam}
          sx={{ mt: 1.5 }}
          variant="outlined"
        >
          Add Parameter
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save Parameters</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// SaveScriptModal
// ─────────────────────────────────────────────────────────────

interface SaveScriptModalProps {
  open: boolean;
  onClose: () => void;
  mode: "save" | "saveAs";
  initialName?: string;
  onSave: (data: { name: string; description: string; category: string }) => void;
  /**
   * The image currently selected in the editor — the one that will be paired
   * with the script at save time. Mirrors PythonTab's `selectedImage` state.
   * Null when no library/user image is selected (which includes the case
   * where a temp disk upload is selected — see `pairedImageIsTemp` below).
   *
   * The PairedImageRow inside the modal reads from this prop and re-renders
   * live when the parent changes the selection (e.g. user clicks "Change" →
   * picks a new image from LibrarySelectModal opened on top).
   */
  pairedImage?: PlaceholderImage | null;
  /**
   * True when the editor has a temp disk upload selected (no persistent id,
   * so it can't be paired with the saved script). Drives the "Temp uploads
   * can't be paired" warning chip. Mutually exclusive with `pairedImage`
   * being non-null — both are derived from the same single-selection state.
   */
  pairedImageIsTemp?: boolean;
  /**
   * Open the library-image picker (LibrarySelectModal). The parent owns the
   * picker — when the user clicks Change/Pick, we call this callback and let
   * the parent open its existing modal on top of this one. MUI handles the
   * z-index stacking. The save modal stays open so the user can continue
   * filling in the dialog after picking.
   */
  onPickPairedImage?: () => void;
  /**
   * Clear the editor's selected image. The parent's `clearImage()` is the
   * authority — this just calls back into it. Used by the Remove button on
   * case 1 (library/user image selected).
   */
  onClearPairedImage?: () => void;
}

const SCRIPT_CATEGORIES = [
  "Segmentation",
  "Measurement",
  "Export",
  "Annotation",
  "Correction",
  "Analysis",
  "Utility",
  "Other",
];

// ── PairedImageRow ─────────────────────────────────────────────
// Compact confirmation row showing what (if anything) will be paired with
// the script when the user clicks Save (or Publish). Three render cases:
//   1. Library/user image  — thumbnail + name + Change/Remove buttons
//   2. Temp disk upload    — thumbnail + warning chip + Change button
//   3. Nothing selected    — dashed placeholder + Pick button
//
// The thumbnail fetches its own blob URL via imageApi.getThumbnailUrl. This
// is its own fetch (not the editor's selectedImageBlobUrl) so the row stays
// self-contained — re-fetches when the paired image id changes, releases
// when the row unmounts (modal close).
//
// Exported so PublishScriptDialog can reuse the same UI pattern (2026-05-15).
// PublishScriptDialog passes isTemp={false} since publishing never deals
// with temp uploads — those can't be associated with a published script.
export function PairedImageRow({
  image,
  isTemp,
  onPick,
  onClear,
}: {
  image: PlaceholderImage | null | undefined;
  isTemp: boolean;
  onPick?: () => void;
  onClear?: () => void;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    setThumbUrl(null);
    if (!image) return;
    const n = parseInt(image.id, 10);
    if (Number.isNaN(n)) return;
    let cancelled = false;
    imageApi.getThumbnailUrl(n).then((url) => {
      if (!cancelled) setThumbUrl(url);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [image?.id]);

  // Case 3 — nothing selected (no temp, no library/user image).
  if (!image && !isTemp) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.25,
          borderRadius: "12px",
          border: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "8px",
            border: "1px dashed",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <ImageOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            No image paired
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Pick a library or user image to pair one with this script.
          </Typography>
        </Box>
        {onPick && (
          <Button size="small" variant="outlined" onClick={onPick}>
            Pick an image
          </Button>
        )}
      </Box>
    );
  }

  // Case 2 — temp disk upload selected (no persistent id, can't be paired).
  if (isTemp) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.25,
          borderRadius: "12px",
          bgcolor: "surfaceContainerLow",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "8px",
            bgcolor: "action.selected",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <ImageOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Temp uploads can't be paired"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem", mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            This script will save without a paired image. Pick a library or user image to pair one.
          </Typography>
        </Box>
        {onPick && (
          <Button size="small" variant="outlined" onClick={onPick}>
            Change
          </Button>
        )}
      </Box>
    );
  }

  // Case 1 — library or user image selected (has persistent id).
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.25,
        borderRadius: "12px",
        bgcolor: "surfaceContainerLow",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "action.selected",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {thumbUrl ? (
          <Box
            component="img"
            src={thumbUrl}
            alt={image?.name ?? "Paired image"}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <CircularProgress size={20} sx={{ color: "primary.main", opacity: 0.6 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {image?.name ?? "Paired image"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          This image will be paired with the script.
        </Typography>
      </Box>
      {onPick && (
        <Button size="small" variant="outlined" onClick={onPick}>
          Change
        </Button>
      )}
      {onClear && (
        <Tooltip title="Remove pairing">
          <IconButton size="small" onClick={onClear} aria-label="Remove paired image">
            <ClearOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export function SaveScriptModal({
  open,
  onClose,
  mode,
  initialName = "",
  onSave,
  pairedImage = null,
  pairedImageIsTemp = false,
  onPickPairedImage,
  onClearPairedImage,
}: SaveScriptModalProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(SCRIPT_CATEGORIES[0]);

  function handleSave() {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description, category });
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ModalTitle title={mode === "save" ? "Save Script" : "Save Script As"} onClose={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Script name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          {/* Paired image preview — shows the editor's current selection so
              the user can see (and adjust) what will be linked at save time. */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.75, fontWeight: 500 }}
            >
              Paired image
            </Typography>
            <PairedImageRow
              image={pairedImage}
              isTemp={pairedImageIsTemp}
              onPick={onPickPairedImage}
              onClear={onClearPairedImage}
            />
          </Box>
          <TextField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            select
            fullWidth
            SelectProps={{ native: true }}
          >
            {SCRIPT_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>
          {mode === "save" ? "Save" : "Save As"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// SaveVersionDialog
//
// Opened when Save is clicked on an already-saved script. Two outlined
// "radio-card" choices stacked vertically:
//
//   ┌─────────────────────────────────────────────────────────┐
//   │ ●  Save changes to v3 (current)                         │
//   │    Overwrite the current version with your edits.       │
//   ├─────────────────────────────────────────────────────────┤
//   │ ○  Create new v4                                        │
//   │    Keep v3 in history and add a new version.            │
//   │    [ Notes (optional) — enabled only for this card ]    │
//   └─────────────────────────────────────────────────────────┘
//
// The notes TextField is rendered inside the create-new card and is only
// enabled when that card is selected — overwrite never takes notes (they
// belong to the row being created, and overwrite reuses the head row).
//
// Purely presentational: returns the user's choice via `onConfirm` and the
// caller (PythonTab) does the appropriate API call (PUT vs POST) + refresh.
// ─────────────────────────────────────────────────────────────

interface SaveVersionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current head version number — shown on the "overwrite" card. */
  currentVersionNumber: number;
  /** Number that a new version row would receive — shown on the "create" card. */
  nextVersionNumber: number;
  /** True while the parent's save/create call is in-flight. */
  saving?: boolean;
  /**
   * Called with the user's choice. For "overwrite", notes is always empty.
   * For "createNew", notes is the trimmed text (empty string allowed).
   */
  onConfirm: (choice: { mode: "overwrite" | "createNew"; notes: string }) => void;
}

const NOTES_MAX = 2000;

type SaveChoice = "overwrite" | "createNew";

export function SaveVersionDialog({
  open,
  onClose,
  currentVersionNumber,
  nextVersionNumber,
  saving = false,
  onConfirm,
}: SaveVersionDialogProps) {
  const [choice, setChoice] = useState<SaveChoice>("overwrite");
  const [notes, setNotes] = useState("");

  // Reset both fields whenever the dialog reopens — otherwise a previous
  // session's choice/notes would leak into the next save.
  useEffect(() => {
    if (open) {
      setChoice("overwrite");
      setNotes("");
    }
  }, [open]);

  const notesTooLong = notes.length > NOTES_MAX;
  const isCreate = choice === "createNew";

  function handleConfirm() {
    if (saving) return;
    if (isCreate && notesTooLong) return;
    onConfirm({ mode: choice, notes: isCreate ? notes.trim() : "" });
  }

  // Outlined radio-card — borderColor + shadow shifts to telegraph the
  // selected state. Same cursor/hover treatment whether selected or not so
  // the user can re-pick at will. Disabled visually when saving so the
  // user doesn't try to switch mid-save.
  function cardSx(selected: boolean) {
    return {
      borderRadius: "12px",
      border: "1px solid",
      borderColor: selected ? "primary.main" : "divider",
      bgcolor: selected ? "action.selected" : "background.paper",
      cursor: saving ? "default" : "pointer",
      p: 1.5,
      transition: "border-color 150ms, background-color 150ms, box-shadow 150ms",
      boxShadow: selected ? "0 0 0 1px var(--mui-palette-primary-main)" : "none",
      opacity: saving ? 0.7 : 1,
      "&:hover": saving
        ? {}
        : { borderColor: selected ? "primary.main" : "text.secondary" },
    };
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <ModalTitle
        title="Save script"
        onClose={saving ? () => {} : onClose}
      />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          How would you like to save your changes?
        </Typography>

        <Stack spacing={1.25}>
          {/* Overwrite card */}
          <Box
            role="radio"
            aria-checked={!isCreate}
            tabIndex={saving ? -1 : 0}
            onClick={() => !saving && setChoice("overwrite")}
            onKeyDown={(e) => {
              if (saving) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setChoice("overwrite");
              }
            }}
            sx={cardSx(!isCreate)}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  mt: 0.25,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: !isCreate ? "primary.main" : "text.disabled",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "border-color 150ms",
                }}
              >
                {!isCreate && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Save changes to v{currentVersionNumber} (current)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                  Overwrite the current version with your edits.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Create-new card */}
          <Box
            role="radio"
            aria-checked={isCreate}
            tabIndex={saving ? -1 : 0}
            onClick={() => !saving && setChoice("createNew")}
            onKeyDown={(e) => {
              if (saving) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setChoice("createNew");
              }
            }}
            sx={cardSx(isCreate)}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  mt: 0.25,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: isCreate ? "primary.main" : "text.disabled",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "border-color 150ms",
                }}
              >
                {isCreate && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Create new v{nextVersionNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                  Keep v{currentVersionNumber} in history and add a new version.
                </Typography>

                {/* Notes field — only enabled when this card is selected.
                    We render it always (rather than conditionally) so the
                    dialog height doesn't jump when toggling cards. Stop
                    propagation on click/keydown so typing inside the field
                    doesn't re-trigger the parent card's onClick. */}
                <Box
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ mt: 1 }}
                >
                  <TextField
                    label="Notes (optional)"
                    placeholder="What changed in this version?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    disabled={!isCreate || saving}
                    error={isCreate && notesTooLong}
                    helperText={
                      isCreate
                        ? notesTooLong
                          ? `${notes.length}/${NOTES_MAX} — too long`
                          : `${notes.length}/${NOTES_MAX}`
                        : " "
                    }
                    size="small"
                    onKeyDown={(e) => {
                      // Cmd/Ctrl+Enter as a power-user shortcut to confirm
                      // — matches the muscle memory from chat inputs.
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                        e.preventDefault();
                        handleConfirm();
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || (isCreate && notesTooLong)}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving
            ? isCreate ? "Creating…" : "Saving…"
            : isCreate ? `Create v${nextVersionNumber}` : `Save v${currentVersionNumber}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
